import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  getAuth,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth'

import {
  signInWithEmailPassword as signInEmailPassword,
  signUpWithEmailPassword as signUpEmailPassword,
  signInWithGooglePopup,
  sendMagicLinkEmail,
  completeMagicLinkSignIn as completeMagicLinkSignInRequest,
  sendPasswordReset as sendPasswordResetRequest,
  signOut as signOutRequest,
} from '../_data/auth'
import {
  getQuickPassUserByFirebaseUid,
  createOrUpdateQuickPassUser,
} from '../_data/quickpass-user'
import {
  buildSyntheticFirebaseUser,
  disableDevAuthBypass,
  isDevAuthBypassEnabled,
  resolveSyntheticQuickPassUserSync,
} from '../_data/dev-auth-bypass'
import {
  clearAllDevLocalStore,
  clearImportedListingsAndNotifications,
} from '../_data/local-store'
import { QuickPassUser, QuickPassAuthProvider } from '../_types/quickpass-user'
import { AuthContextValue, AuthProviderProps } from './auth-context-types'
import { AuthContext } from './use-auth'

/**
 * Wraps the app, listens to Firebase Auth state, and resolves the matching
 * QuickPass user record from the backend (creating it on first sign-in).
 * Exposes auth actions via Context so components never touch Firebase Auth
 * or the data layer directly.
 *
 * @param {AuthProviderProps} props
 * @returns {JSX.Element}
 */
const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const isBypassActive = isDevAuthBypassEnabled()

  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(() => {
    if (isBypassActive === true) {
      return buildSyntheticFirebaseUser() as unknown as FirebaseUser
    }
    return null
  })
  const [quickPassUser, setQuickPassUser] = useState<QuickPassUser | null>(
    () => {
      if (isBypassActive === true) {
        return resolveSyntheticQuickPassUserSync()
      }
      return null
    },
  )
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    return isBypassActive === false
  })
  const lastResolvedUidReference = useRef<string | null>(
    isBypassActive === true
      ? (buildSyntheticFirebaseUser() as unknown as FirebaseUser).uid
      : null,
  )

  useEffect(() => {
    if (isDevAuthBypassEnabled() === true) {
      return undefined
    }

    const auth = getAuth()

    const unsubscribe = onAuthStateChanged(auth, async incomingUser => {
      setFirebaseUser(incomingUser)

      if (incomingUser === null) {
        setQuickPassUser(null)
        lastResolvedUidReference.current = null
        setIsLoading(false)
        return
      }

      if (lastResolvedUidReference.current === incomingUser.uid) {
        setIsLoading(false)
        return
      }

      lastResolvedUidReference.current = incomingUser.uid
      clearAllDevLocalStore()

      try {
        let resolvedUser = await getQuickPassUserByFirebaseUid(incomingUser.uid)

        if (resolvedUser === null) {
          resolvedUser = await createOrUpdateQuickPassUser({
            firebaseAuthUid: incomingUser.uid,
            email: incomingUser.email ?? '',
            phone: incomingUser.phoneNumber,
            displayName: incomingUser.displayName,
            authProviders: deriveAuthProviders(incomingUser),
          })
        }

        setQuickPassUser(resolvedUser)
        clearImportedListingsAndNotifications(resolvedUser.id)
      } catch (error) {
        console.error('Failed to resolve QuickPass user', error)
        setQuickPassUser(null)
      } finally {
        setIsLoading(false)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const signInWithEmailPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      await signInEmailPassword(email, password)
    },
    [],
  )

  const signUpWithEmailPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      await signUpEmailPassword(email, password)
    },
    [],
  )

  const signInWithGoogle = useCallback(async (): Promise<void> => {
    await signInWithGooglePopup()
  }, [])

  const sendMagicLink = useCallback(async (email: string): Promise<void> => {
    await sendMagicLinkEmail(email)
  }, [])

  const completeMagicLinkSignIn = useCallback(
    async (currentUrl: string, fallbackEmail: string | null): Promise<void> => {
      await completeMagicLinkSignInRequest(currentUrl, fallbackEmail)
    },
    [],
  )

  const sendPasswordReset = useCallback(
    async (email: string): Promise<void> => {
      await sendPasswordResetRequest(email)
    },
    [],
  )

  const signOut = useCallback(async (): Promise<void> => {
    if (isDevAuthBypassEnabled() === true) {
      disableDevAuthBypass()
      return
    }

    await signOutRequest()
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      quickPassUser,
      isLoading,
      isAuthenticated: firebaseUser !== null,
      signInWithEmailPassword,
      signUpWithEmailPassword,
      signInWithGoogle,
      sendMagicLink,
      completeMagicLinkSignIn,
      sendPasswordReset,
      signOut,
    }),
    [
      firebaseUser,
      quickPassUser,
      isLoading,
      signInWithEmailPassword,
      signUpWithEmailPassword,
      signInWithGoogle,
      sendMagicLink,
      completeMagicLinkSignIn,
      sendPasswordReset,
      signOut,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Maps Firebase Auth providerData entries to QuickPass auth-provider tags.
 * Note: Firebase reports both regular email/password sign-ins and magic-link
 * sign-ins under providerId `'password'`, so they collapse to
 * `'email-password'` here. Fine-grained magic-link tracking would need a
 * session flag set during the actual flow — deferred until product needs it.
 *
 * @param {FirebaseUser} user
 * @returns {QuickPassAuthProvider[]}
 */
const deriveAuthProviders = (user: FirebaseUser): QuickPassAuthProvider[] => {
  const providers: QuickPassAuthProvider[] = []

  for (const providerData of user.providerData) {
    if (providerData.providerId === 'password') {
      providers.push('email-password')
    } else if (providerData.providerId === 'google.com') {
      providers.push('google')
    }
  }

  return providers
}

export { AuthProvider }
